import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Tooltip,
  Stack,
  InputAdornment,
  Checkbox,
  Menu,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LayersIcon from '@mui/icons-material/Layers';
import StadiumIcon from '@mui/icons-material/Stadium';
import CategoryIcon from '@mui/icons-material/Category';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { API_URL } from '../config';
import { fetchApi, authedFetch } from '../utils/apiUtils';
import { ConfirmDialog } from './common/ConfirmDialog';

interface Track {
  track_id: number;
  track_name: string;
  track_city: string;
  track_state: string;
}

interface Division {
  division_id: number;
  division_name: string;
}

interface Region {
  region_id: number;
  region_name: string;
}

interface TrackAssignment {
  assignment_id: number;
  track_id: number;
  track_name: string;
  division_id: number;
  division_name: string;
  region_id: number | null;
  region_name: string | null;
  season_year: number;
  is_active: boolean | number;
}

interface AssignmentFormData {
  assignment_id?: number;
  track_id: number | '';
  division_id: number | '';
  region_id: number | '';
  season_year: number;
  is_active: boolean;
}

interface BulkAssignmentForm {
  division_id: number | '';
  region_id: number | '';
  season_year: number;
  is_active: boolean;
  selected_tracks: number[];
}

function TrackAssignments(): React.ReactElement {
  const configuredSeasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();

  const [assignments, setAssignments] = useState<TrackAssignment[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<AssignmentFormData | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TrackAssignment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [bulkDialogError, setBulkDialogError] = useState<string | null>(null);
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);

  const [bulkForm, setBulkForm] = useState<BulkAssignmentForm>({
    division_id: '',
    region_id: '',
    season_year: configuredSeasonYear,
    is_active: true,
    selected_tracks: [],
  });

  const [trackSearch, setTrackSearch] = useState('');
  const [availableRegionsInDialog, setAvailableRegionsInDialog] = useState<Region[]>([]);
  const [availableRegionsInBulkDialog, setAvailableRegionsInBulkDialog] = useState<Region[]>([]);

  useEffect(() => {
    setBulkForm((prev) => ({ ...prev, season_year: configuredSeasonYear }));
  }, [configuredSeasonYear]);

  useEffect(() => {
    fetchAssignments();
    fetchTracks();
    fetchDivisions();
  }, []);

  const fetchAssignments = async (): Promise<void> => {
    try {
      const data = (await fetchApi(`${API_URL}/track-assignments`)) as TrackAssignment[] & { error?: string };
      if (data.error) {
        setError(data.error);
        return;
      }
      setAssignments(data);
    } catch (err) {
      console.error('Error fetching track assignments:', err);
      setError('Failed to fetch track assignments');
    }
  };

  const fetchTracks = async (): Promise<void> => {
    try {
      const data = (await fetchApi(`${API_URL}/tracks`)) as Track[];
      setTracks(data);
    } catch (err) {
      console.error('Error fetching tracks:', err);
    }
  };

  const fetchDivisions = async (): Promise<void> => {
    try {
      const data = (await fetchApi(`${API_URL}/divisions`)) as Division[];
      setDivisions(data);
    } catch (err) {
      console.error('Error fetching divisions:', err);
    }
  };

  const dialogRegionReqId = useRef(0);
  const bulkRegionReqId = useRef(0);

  const fetchAvailableRegionsForDivision = useCallback(
    async (
      divisionId: number | '',
      seasonYear: number,
      requestIdRef: React.MutableRefObject<number>,
      setAvailable: React.Dispatch<React.SetStateAction<Region[]>>,
    ): Promise<void> => {
      setAvailable([]);
      if (!divisionId) return;
      const myRequestId = ++requestIdRef.current;
      try {
        const response = await authedFetch(
          `${API_URL}/division-regions/${divisionId}?season_year=${seasonYear}`,
        );
        const links = await response.json();
        if (myRequestId !== requestIdRef.current) return;
        if (response.ok) {
          const fromResponse: Region[] = (links as Array<{ region_id: number; region_name?: string }>).map(
            (link) => ({
              region_id: link.region_id,
              region_name: link.region_name ?? `Region ${link.region_id}`,
            }),
          );
          setAvailable(fromResponse);
        } else {
          setError(links.error || 'Failed to load regions for division');
        }
      } catch (err) {
        if (myRequestId !== requestIdRef.current) return;
        console.error('Error fetching division-regions:', err);
        setError('Error loading regions for selected division');
      }
    },
    [],
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    assignments.forEach((a) => years.add(a.season_year));
    years.add(configuredSeasonYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [assignments, configuredSeasonYear]);

  const seasonAssignments = useMemo(
    () => assignments.filter((a) => a.season_year === configuredSeasonYear),
    [assignments, configuredSeasonYear],
  );

  const stats = useMemo(() => {
    const total = seasonAssignments.length;
    const distinctTracks = new Set(seasonAssignments.map((a) => a.track_id)).size;
    const distinctDivisions = new Set(seasonAssignments.map((a) => a.division_id)).size;
    const inactive = seasonAssignments.filter((a) => !a.is_active).length;
    return [
      { label: 'Total Assignments', value: total.toLocaleString(), icon: <LayersIcon /> },
      { label: 'Tracks Covered', value: distinctTracks.toLocaleString(), icon: <StadiumIcon /> },
      { label: 'Divisions In Use', value: distinctDivisions.toLocaleString(), icon: <CategoryIcon /> },
      { label: 'Inactive', value: inactive.toLocaleString(), icon: <PublicIcon /> },
    ];
  }, [seasonAssignments]);

  const groupedAssignments = useMemo(() => {
    const grouped = new Map<
      number,
      {
        division_name: string;
        regions: Map<string, { region_name: string; tracks: TrackAssignment[] }>;
      }
    >();
    seasonAssignments.forEach((a) => {
      if (!grouped.has(a.division_id)) {
        grouped.set(a.division_id, { division_name: a.division_name, regions: new Map() });
      }
      const div = grouped.get(a.division_id)!;
      const regionKey = a.region_id ? `r${a.region_id}` : 'no-region';
      if (!div.regions.has(regionKey)) {
        div.regions.set(regionKey, {
          region_name: a.region_name || 'No region',
          tracks: [],
        });
      }
      div.regions.get(regionKey)!.tracks.push(a);
    });
    return grouped;
  }, [seasonAssignments]);

  const handleOpenAdd = (): void => {
    setEditingAssignment({
      track_id: '',
      division_id: '',
      region_id: '',
      season_year: configuredSeasonYear,
      is_active: true,
    });
    setAvailableRegionsInDialog([]);
    setDialogError(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (assignment: TrackAssignment): void => {
    setEditingAssignment({
      assignment_id: assignment.assignment_id,
      track_id: assignment.track_id,
      division_id: assignment.division_id,
      region_id: assignment.region_id ?? '',
      season_year: assignment.season_year,
      is_active: Boolean(assignment.is_active),
    });
    if (assignment.division_id) {
      fetchAvailableRegionsForDivision(
        assignment.division_id,
        assignment.season_year,
        dialogRegionReqId,
        setAvailableRegionsInDialog,
      );
    } else {
      setAvailableRegionsInDialog([]);
    }
    setDialogError(null);
    setOpenDialog(true);
  };

  const handleSaveAssignment = async (): Promise<void> => {
    if (!editingAssignment) return;

    if (!editingAssignment.track_id || !editingAssignment.division_id || !editingAssignment.season_year) {
      setDialogError('Track, division, and season year are required.');
      return;
    }
    if (
      editingAssignment.division_id &&
      availableRegionsInDialog.length > 0 &&
      !editingAssignment.region_id
    ) {
      setDialogError('Pick a region for this division.');
      return;
    }

    setIsSaving(true);
    try {
      const isEdit = Boolean(editingAssignment.assignment_id);
      const url = isEdit
        ? `${API_URL}/track-assignments/${editingAssignment.assignment_id}`
        : `${API_URL}/track-assignments`;
      const response = await authedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingAssignment),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setDialogError(data.error || 'Failed to save assignment.');
        return;
      }
      await fetchAssignments();
      setOpenDialog(false);
      setEditingAssignment(null);
    } catch (err) {
      console.error('Error saving track assignment:', err);
      setDialogError('Failed to save assignment.');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDeleteAssignment = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await authedFetch(`${API_URL}/track-assignments/${deleteTarget.assignment_id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to delete track assignment');
        return;
      }
      setAssignments((prev) => prev.filter((a) => a.assignment_id !== deleteTarget.assignment_id));
    } catch (err) {
      console.error('Error deleting track assignment:', err);
      setError('Failed to delete track assignment');
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleBulkAssignment = async (): Promise<void> => {
    if (!bulkForm.division_id) {
      setBulkDialogError('Pick a division.');
      return;
    }
    if (availableRegionsInBulkDialog.length > 0 && !bulkForm.region_id) {
      setBulkDialogError('Pick a region for this division.');
      return;
    }
    if (bulkForm.selected_tracks.length === 0) {
      setBulkDialogError('Select at least one track.');
      return;
    }

    setIsBulkSaving(true);
    try {
      const results = await Promise.all(
        bulkForm.selected_tracks.map(async (track_id) => {
          const res = await authedFetch(`${API_URL}/track-assignments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              track_id,
              division_id: bulkForm.division_id,
              region_id: bulkForm.region_id || null,
              season_year: bulkForm.season_year,
              is_active: bulkForm.is_active,
            }),
          });
          const data = await res.json().catch(() => ({}));
          return { ok: res.ok, error: data.error ?? data.message };
        }),
      );
      const failures = results.filter((r) => !r.ok);
      if (failures.length > 0) {
        setBulkDialogError(
          `Failed to assign ${failures.length} of ${bulkForm.selected_tracks.length} tracks. ${
            failures[0].error ?? 'They may already be assigned.'
          }`,
        );
      }
      await fetchAssignments();
      if (failures.length === 0) {
        setOpenBulkDialog(false);
        setBulkForm({
          division_id: '',
          region_id: '',
          season_year: configuredSeasonYear,
          is_active: true,
          selected_tracks: [],
        });
        setAvailableRegionsInBulkDialog([]);
      }
    } catch (err) {
      console.error('Error performing bulk assignment:', err);
      setBulkDialogError('Failed to perform bulk assignment.');
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleTrackToggle = (track_id: number): void => {
    setBulkForm((prev) => {
      const next = new Set(prev.selected_tracks);
      if (next.has(track_id)) next.delete(track_id);
      else next.add(track_id);
      return { ...prev, selected_tracks: Array.from(next) };
    });
  };

  const filteredTracks = useMemo(() => {
    const q = trackSearch.toLowerCase();
    if (!q) return tracks;
    return tracks.filter(
      (t) =>
        (t.track_name ?? '').toLowerCase().includes(q) ||
        (t.track_city ?? '').toLowerCase().includes(q) ||
        (t.track_state ?? '').toLowerCase().includes(q),
    );
  }, [tracks, trackSearch]);

  const renderRegionSelectItems = (
    divisionId: number | '',
    available: Region[],
  ): React.ReactNode => {
    if (!divisionId) {
      return (
        <MenuItem value="" disabled>
          <em>Pick a division first</em>
        </MenuItem>
      );
    }
    if (available.length === 0) {
      return (
        <MenuItem value="">
          <em>No region (division has no regions assigned)</em>
        </MenuItem>
      );
    }
    return [
      <MenuItem key="placeholder" value="">
        <em>Select a region</em>
      </MenuItem>,
      ...available.map((region) => (
        <MenuItem key={region.region_id} value={region.region_id}>
          {region.region_name}
        </MenuItem>
      )),
    ];
  };

  const yearSelectOptions = useMemo(() => {
    const years: number[] = [];
    for (let i = 1; i >= -3; i -= 1) years.push(configuredSeasonYear + i);
    return years;
  }, [configuredSeasonYear]);

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Track Assignments
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {configuredSeasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === configuredSeasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              setBulkForm({
                division_id: '',
                region_id: '',
                season_year: configuredSeasonYear,
                is_active: true,
                selected_tracks: [],
              });
              setAvailableRegionsInBulkDialog([]);
              setBulkDialogError(null);
              setOpenBulkDialog(true);
            }}
          >
            Bulk Assign
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add Assignment
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', '& svg': { fontSize: 18 } }}>
                    {stat.icon}
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {groupedAssignments.size === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No track assignments for {configuredSeasonYear}. Use Add or Bulk Assign to get started.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {Array.from(groupedAssignments.entries()).map(([divisionId, division]) => (
            <Paper key={divisionId} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {division.division_name}
                </Typography>
                <Chip
                  label={`${Array.from(division.regions.values()).reduce(
                    (sum, r) => sum + r.tracks.length,
                    0,
                  )} tracks`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
              <Stack spacing={2}>
                {Array.from(division.regions.entries()).map(([regionKey, region]) => (
                  <Box key={regionKey}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: '0.04em', fontWeight: 500, display: 'block', mb: 0.75 }}
                    >
                      {region.region_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {region.tracks.map((assignment) => (
                        <Tooltip key={assignment.assignment_id} title={assignment.is_active ? 'Active' : 'Inactive'}>
                          <Chip
                            label={assignment.track_name}
                            size="small"
                            variant="outlined"
                            color={assignment.is_active ? 'primary' : 'default'}
                            onClick={() => handleOpenEdit(assignment)}
                            onDelete={() => setDeleteTarget(assignment)}
                            deleteIcon={<DeleteIcon />}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog
        open={openDialog}
        onClose={() => {
          if (isSaving) return;
          setOpenDialog(false);
          setEditingAssignment(null);
          setDialogError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAssignment?.assignment_id ? 'Edit Assignment' : 'Add Assignment'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Track</InputLabel>
              <Select
                value={editingAssignment?.track_id ?? ''}
                label="Track"
                onChange={(e: SelectChangeEvent<number | ''>) =>
                  setEditingAssignment(
                    (prev) => prev && { ...prev, track_id: e.target.value as number | '' },
                  )
                }
              >
                {tracks.map((track) => (
                  <MenuItem key={track.track_id} value={track.track_id}>
                    {track.track_name}
                    {track.track_city || track.track_state
                      ? ` — ${[track.track_city, track.track_state].filter(Boolean).join(', ')}`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Division</InputLabel>
              <Select
                value={editingAssignment?.division_id ?? ''}
                label="Division"
                onChange={(e: SelectChangeEvent<number | ''>) => {
                  const divisionId = e.target.value as number | '';
                  setEditingAssignment(
                    (prev) => prev && { ...prev, division_id: divisionId, region_id: '' },
                  );
                  fetchAvailableRegionsForDivision(
                    divisionId,
                    editingAssignment?.season_year ?? configuredSeasonYear,
                    dialogRegionReqId,
                    setAvailableRegionsInDialog,
                  );
                }}
              >
                {divisions.map((division) => (
                  <MenuItem key={division.division_id} value={division.division_id}>
                    {division.division_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!editingAssignment?.division_id}>
              <InputLabel>Region</InputLabel>
              <Select
                value={editingAssignment?.region_id ?? ''}
                label="Region"
                onChange={(e: SelectChangeEvent<number | ''>) =>
                  setEditingAssignment(
                    (prev) => prev && { ...prev, region_id: e.target.value as number | '' },
                  )
                }
              >
                {renderRegionSelectItems(
                  editingAssignment?.division_id ?? '',
                  availableRegionsInDialog,
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Season Year</InputLabel>
              <Select
                value={editingAssignment?.season_year ?? configuredSeasonYear}
                label="Season Year"
                onChange={(e: SelectChangeEvent<number>) => {
                  const newYear = e.target.value as number;
                  setEditingAssignment(
                    (prev) => prev && { ...prev, season_year: newYear, region_id: '' },
                  );
                  if (editingAssignment?.division_id) {
                    fetchAvailableRegionsForDivision(
                      editingAssignment.division_id,
                      newYear,
                      dialogRegionReqId,
                      setAvailableRegionsInDialog,
                    );
                  }
                }}
              >
                {yearSelectOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editingAssignment?.is_active ?? true}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEditingAssignment(
                      (prev) => prev && { ...prev, is_active: e.target.checked },
                    )
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isSaving}
            onClick={() => {
              setOpenDialog(false);
              setEditingAssignment(null);
              setDialogError(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveAssignment} variant="contained" disabled={isSaving}>
            {isSaving ? 'Saving' : editingAssignment?.assignment_id ? 'Save changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBulkDialog}
        onClose={() => {
          if (isBulkSaving) return;
          setOpenBulkDialog(false);
          setBulkDialogError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Assign Tracks</DialogTitle>
        <DialogContent>
          {bulkDialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkDialogError(null)}>
              {bulkDialogError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Division</InputLabel>
                <Select
                  value={bulkForm.division_id}
                  label="Division"
                  onChange={(e: SelectChangeEvent<number | ''>) => {
                    const divisionId = e.target.value as number | '';
                    setBulkForm((prev) => ({ ...prev, division_id: divisionId, region_id: '' }));
                    fetchAvailableRegionsForDivision(
                      divisionId,
                      bulkForm.season_year,
                      bulkRegionReqId,
                      setAvailableRegionsInBulkDialog,
                    );
                  }}
                >
                  {divisions.map((division) => (
                    <MenuItem key={division.division_id} value={division.division_id}>
                      {division.division_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={!bulkForm.division_id}>
                <InputLabel>Region</InputLabel>
                <Select
                  value={bulkForm.region_id}
                  label="Region"
                  onChange={(e: SelectChangeEvent<number | ''>) =>
                    setBulkForm((prev) => ({ ...prev, region_id: e.target.value as number | '' }))
                  }
                >
                  {renderRegionSelectItems(bulkForm.division_id, availableRegionsInBulkDialog)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Season Year</InputLabel>
                <Select
                  value={bulkForm.season_year}
                  label="Season Year"
                  onChange={(e: SelectChangeEvent<number>) => {
                    const newYear = e.target.value as number;
                    setBulkForm((prev) => ({
                      ...prev,
                      season_year: newYear,
                      region_id: '',
                    }));
                    if (bulkForm.division_id) {
                      fetchAvailableRegionsForDivision(
                        bulkForm.division_id,
                        newYear,
                        bulkRegionReqId,
                        setAvailableRegionsInBulkDialog,
                      );
                    }
                  }}
                >
                  {yearSelectOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkForm.is_active}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setBulkForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                }
                label="Active"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: '0.04em', fontWeight: 500, display: 'block', mb: 1 }}
              >
                Tracks to assign · {bulkForm.selected_tracks.length} selected
              </Typography>
              {bulkForm.selected_tracks.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  {bulkForm.selected_tracks.map((trackId) => {
                    const track = tracks.find((t) => t.track_id === trackId);
                    return track ? (
                      <Chip
                        key={track.track_id}
                        label={track.track_name}
                        onDelete={() => handleTrackToggle(track.track_id)}
                        size="small"
                        variant="outlined"
                      />
                    ) : null;
                  })}
                </Box>
              )}
              <TextField
                fullWidth
                size="small"
                placeholder="Search tracks..."
                value={trackSearch}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setTrackSearch(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto' }}>
                <List dense disablePadding>
                  {filteredTracks.length > 0 ? (
                    filteredTracks.map((track) => {
                      const isSelected = bulkForm.selected_tracks.includes(track.track_id);
                      return (
                        <ListItem key={track.track_id} disablePadding>
                          <ListItemButton
                            role="checkbox"
                            aria-checked={isSelected}
                            selected={isSelected}
                            onClick={() => handleTrackToggle(track.track_id)}
                            sx={{ px: 2, py: 0.75 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-labelledby': `bulk-track-${track.track_id}` }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              id={`bulk-track-${track.track_id}`}
                              primary={track.track_name}
                              secondary={[track.track_city, track.track_state]
                                .filter(Boolean)
                                .join(', ')}
                              primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })
                  ) : (
                    <ListItem>
                      <ListItemText primary="No tracks found" secondary="Try a different search." />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isBulkSaving}
            onClick={() => {
              setOpenBulkDialog(false);
              setBulkDialogError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssignment}
            variant="contained"
            disabled={
              isBulkSaving ||
              !bulkForm.division_id ||
              (availableRegionsInBulkDialog.length > 0 && !bulkForm.region_id) ||
              bulkForm.selected_tracks.length === 0
            }
          >
            {isBulkSaving
              ? 'Assigning'
              : `Assign ${bulkForm.selected_tracks.length || ''} tracks`.trim()}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Track Assignment"
        message={
          deleteTarget
            ? `Remove ${deleteTarget.track_name} from ${deleteTarget.division_name}${
                deleteTarget.region_name ? ` · ${deleteTarget.region_name}` : ''
              } for ${deleteTarget.season_year}?`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeleting}
        onConfirm={confirmDeleteAssignment}
        onCancel={() => setDeleteTarget(null)}
      />

      <Snackbar
        open={!!error && !openDialog && !openBulkDialog && !deleteTarget}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TrackAssignments;
